import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import ActionIconButton from '../../components/ActionIconButton';
import { deleteHrRow, getHrRows, saveHrRow } from '../../service/apiService';
import './UserManagement.css';

const entityConfigs = {
  employees: {
    title: 'Employee Management',
    fields: ['employee_code', 'name', 'email', 'phone', 'department', 'designation', 'joining_date', 'status'],
    required: ['employee_code', 'name'],
  },
  departments: { title: 'Departments', fields: ['name', 'description', 'status'], required: ['name'] },
  designations: { title: 'Designations', fields: ['name', 'department', 'description', 'status'], required: ['name'] },
  attendance: { title: 'Attendance', fields: ['employee_id', 'attendance_date', 'check_in_time', 'check_out_time', 'status', 'remarks'], required: ['employee_id', 'attendance_date'] },
  leaves: { title: 'Leave Management', fields: ['employee_id', 'leave_type', 'from_date', 'to_date', 'reason', 'status', 'approved_by'], required: ['employee_id', 'from_date', 'to_date'] },
  payroll: { title: 'Payroll', fields: ['employee_id', 'month_year', 'basic', 'allowances', 'deductions', 'net_salary', 'status'], required: ['employee_id', 'month_year'] },
  holidays: { title: 'Holidays', fields: ['name', 'holiday_date', 'description', 'status'], required: ['name', 'holiday_date'] },
  notices: { title: 'Notices', fields: ['title', 'message', 'audience', 'posted_by', 'status'], required: ['title'] },
  documents: { title: 'Employee Documents', fields: ['employee_id', 'document_type', 'title', 'file_name', 'file_path', 'notes'], required: ['employee_id', 'title'] },
  reviews: { title: 'Performance Reviews', fields: ['employee_id', 'review_period', 'rating', 'review_notes', 'reviewed_by', 'status'], required: ['employee_id', 'review_period'] },
};

const emptyFor = (entity) => entityConfigs[entity].fields.reduce((acc, field) => ({ ...acc, [field]: '' }), { id: 0 });

function labelize(field) {
  return field.replaceAll('_', ' ');
}

function inputType(field) {
  if (field.includes('date')) return 'date';
  if (field.includes('time')) return 'time';
  if (['basic', 'allowances', 'deductions', 'net_salary', 'rating', 'employee_id'].includes(field)) return 'number';
  if (field === 'month_year') return 'month';
  if (field === 'email') return 'email';
  return 'text';
}

function HrModule() {
  const [activeEntity, setActiveEntity] = useState('employees');
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyFor('employees'));
  const [showModal, setShowModal] = useState(false);

  const config = useMemo(() => entityConfigs[activeEntity], [activeEntity]);

  const loadRows = async (page = 1) => {
    setLoading(true);
    try {
      const [listRes, reportRes] = await Promise.all([
        getHrRows(activeEntity, { search, page, limit: pagination.limit }),
        getHrRows('reports'),
      ]);
      if (listRes.status === 'success') {
        setRows(listRes.data.rows || []);
        setPagination(listRes.data.pagination || pagination);
      }
      if (reportRes.status === 'success') setReports(reportRes.data);
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    const [departmentRes, designationRes] = await Promise.all([
      getHrRows('departments', { limit: 100 }),
      getHrRows('designations', { limit: 100 }),
    ]);
    if (departmentRes.status === 'success') {
      setDepartments((departmentRes.data.rows || []).filter((item) => (item.status || 'active') === 'active'));
    }
    if (designationRes.status === 'success') {
      setDesignations((designationRes.data.rows || []).filter((item) => (item.status || 'active') === 'active'));
    }
  };

  useEffect(() => {
    setForm(emptyFor(activeEntity));
    loadRows(1);
    if (activeEntity === 'employees') loadLookups();
  }, [activeEntity, search]);

  const openCreate = () => {
    setForm(emptyFor(activeEntity));
    if (activeEntity === 'employees') loadLookups();
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({ ...emptyFor(activeEntity), ...row });
    if (activeEntity === 'employees') loadLookups();
    setShowModal(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const res = await saveHrRow(activeEntity, form);
    if (res.status === 'success') {
      setShowModal(false);
      await loadRows(pagination.page);
      if (activeEntity === 'departments' || activeEntity === 'designations') await loadLookups();
    } else {
      alert(res.message || 'Unable to save record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    const res = await deleteHrRow(activeEntity, id);
    if (res.status === 'success') await loadRows(pagination.page);
    else alert(res.message || 'Unable to delete record');
  };

  const departmentNames = departments.map((department) => department.name).filter(Boolean);
  const designationNames = designations
    .filter((designation) => !form.department || !designation.department || designation.department === form.department)
    .map((designation) => designation.name)
    .filter(Boolean);

  const renderFieldControl = (field) => {
    if (activeEntity === 'employees' && field === 'department') {
      return (
        <Form.Select
          value={form.department || ''}
          onChange={(e) => setForm({ ...form, department: e.target.value, designation: '' })}
          required={config.required.includes(field)}
        >
          <option value="">{departmentNames.length ? 'Select department' : 'Add department first'}</option>
          {departmentNames.map((name) => <option key={name} value={name}>{name}</option>)}
        </Form.Select>
      );
    }

    if (activeEntity === 'employees' && field === 'designation') {
      return (
        <Form.Select
          value={form.designation || ''}
          onChange={(e) => setForm({ ...form, designation: e.target.value })}
          required={config.required.includes(field)}
        >
          <option value="">{designationNames.length ? 'Select designation' : 'Add designation first'}</option>
          {designationNames.map((name) => <option key={name} value={name}>{name}</option>)}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        as={field === 'message' || field === 'review_notes' || field === 'notes' ? 'textarea' : 'input'}
        rows={3}
        type={inputType(field)}
        value={form[field] || ''}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        required={config.required.includes(field)}
      />
    );
  };

  return (
    <div className="hr-module-page">
      <div className="page-title-row">
        <div>
          <span>Operations</span>
          <h3>HR Module</h3>
        </div>
        <Button onClick={openCreate}>Add {config.title}</Button>
      </div>

      <Row className="g-3">
        {[
          ['Employees', reports?.employees || 0],
          ['Active', reports?.active_employees || 0],
          ['Pending Leaves', reports?.pending_leaves || 0],
          ['Payroll', `Rs. ${Number(reports?.current_payroll || 0).toFixed(0)}`],
        ].map(([title, value]) => (
          <Col md={3} sm={6} key={title}>
            <Card className="erp-panel-card"><Card.Body><span className="text-muted small">{title}</span><h4 className="mb-0">{value}</h4></Card.Body></Card>
          </Col>
        ))}
      </Row>

      <Tabs activeKey={activeEntity} onSelect={(key) => setActiveEntity(key || 'employees')} className="erp-tabs">
        {Object.entries(entityConfigs).map(([key, item]) => <Tab key={key} eventKey={key} title={item.title} />)}
      </Tabs>

      <Card className="erp-panel-card">
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={5}><Form.Control placeholder={`Search ${config.title.toLowerCase()}`} value={search} onChange={(e) => setSearch(e.target.value)} /></Col>
          </Row>

          {loading ? <div className="table-loader"><Spinner animation="border" /></div> : (
            <Table responsive hover className="erp-table">
              <thead>
                <tr>
                  {config.fields.slice(0, 6).map((field) => <th key={field}>{labelize(field)}</th>)}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {config.fields.slice(0, 6).map((field) => (
                      <td key={field}>
                        {field === 'status' ? <Badge bg={row[field] === 'active' || row[field] === 'approved' || row[field] === 'paid' ? 'success' : 'secondary'}>{row[field] || '-'}</Badge> : row[field] || '-'}
                      </td>
                    ))}
                    <td>
                      <div className="erp-action-row">
                        <ActionIconButton size="sm" variant="outline-primary" icon={faPenToSquare} label="Edit" onClick={() => openEdit(row)} />
                        <ActionIconButton size="sm" variant="outline-danger" icon={faTrash} label="Delete" onClick={() => handleDelete(row.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={config.fields.slice(0, 6).length + 1} className="text-center text-muted">No records found</td></tr>}
              </tbody>
            </Table>
          )}

          {pagination.total_pages > 1 && (
            <Pagination className="justify-content-end">
              <Pagination.Prev disabled={pagination.page <= 1} onClick={() => loadRows(pagination.page - 1)} />
              {[...Array(pagination.total_pages)].map((_, index) => (
                <Pagination.Item key={index + 1} active={pagination.page === index + 1} onClick={() => loadRows(index + 1)}>{index + 1}</Pagination.Item>
              ))}
              <Pagination.Next disabled={pagination.page >= pagination.total_pages} onClick={() => loadRows(pagination.page + 1)} />
            </Pagination>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton><Modal.Title>{form.id ? 'Edit' : 'Add'} {config.title}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              {config.fields.map((field) => (
                <Col md={field === 'message' || field === 'review_notes' || field === 'notes' ? 12 : 6} key={field}>
                  <Form.Label className="text-capitalize">{labelize(field)}</Form.Label>
                  {renderFieldControl(field)}
                </Col>
              ))}
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button type="submit">Save</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default HrModule;
